/**
 * syncEngine — drains the offline outbox to the server.
 *
 * Each queued op has a `type` mapped to a replay handler below. Handlers talk
 * to the real API; if a handler hits a *network* error the op stays queued and
 * draining stops (we're offline again). If it hits a *server rejection*
 * (validation / permission), the op is flagged 'error' with the message and
 * left for the user to see — it won't auto-retry forever.
 *
 * A tiny pub/sub lets the UI show the pending count and a "syncing" state.
 */
import { api, ApiError } from '@/api/apiClient';
import { listOps, removeOp, updateOp } from '@/lib/outbox';

// ── Replay handlers (per op type) ────────────────────────────────────────
const HANDLERS = {
  // Sales order: create (draft) then optionally confirm — chained with the
  // REAL id the server returns, so confirmation still works after sync.
  order: async ({ createBody, confirm }) => {
    const r = await api.post('/sales/orders', createBody);
    if (!r?.success) throw new ServerRejection(r?.error || 'فشل إنشاء الطلب');
    const order = r.order || r.data;
    if (confirm && order?.id) {
      // Non-fatal: if confirm fails the order still exists as a draft.
      try { await api.post('/sellgo_v5_order_confirm', { order_id: order.id }); } catch { /* draft */ }
    }
    return order;
  },
  // Placeholders for phase 3b (collections, visits, returns, gifts) are added
  // here when those screens start enqueuing.

  // Customer payment / collection. Try the SellGo endpoint, fall back to the
  // legacy one (mirrors the online screen's logic).
  collection: async ({ body }) => {
    try {
      const r = await api.post('/sellgo_payment_create', body);
      if (r?.success) return r.data;
    } catch (e) {
      if (isNetworkError(e)) throw e; // retry later
      // non-network error → fall through to the legacy endpoint
    }
    const r2 = await api.post('/collections/payments', {
      partner_id: body.partner_id,
      amount: body.amount,
      payment_date: body.date,
      memo: body.ref || body.communication || '',
      invoice_ids: body.invoice_ids,
    });
    if (!r2?.success) throw new ServerRejection(r2?.error || 'فشل تسجيل التحصيل');
    return r2.data;
  },

  // Product return request.
  return: async ({ body }) => {
    const r = await api.post('/sellgo_v5_return_create', body);
    if (!r?.success) throw new ServerRejection(r?.error || 'فشل إنشاء المرتجع');
    return r.data;
  },

  // Gift dispense / request.
  gift: async ({ body }) => {
    const r = await api.post('/sellgo_v5_gift_create', body);
    if (!r?.success) throw new ServerRejection(r?.error || 'فشل إنشاء الهدية');
    return r.data;
  },
};

/** Thrown by a handler when the server rejects an op (not a network failure). */
export class ServerRejection extends Error {
  constructor(message) { super(message); this.name = 'ServerRejection'; }
}

function isNetworkError(e) {
  // ApiError with status 0 is our network/timeout marker.
  return e instanceof ApiError && (!e.status || e.status === 0);
}

// ── Pub/sub for the pending count ────────────────────────────────────────
const listeners = new Set();
let _state = { pending: 0, syncing: false };

function emit() {
  for (const fn of listeners) { try { fn(_state); } catch { /* ignore */ } }
}

export function subscribeSync(fn) {
  listeners.add(fn);
  fn(_state);
  return () => listeners.delete(fn);
}

async function refreshCount() {
  const ops = await listOps();
  _state = { ..._state, pending: ops.length };
  emit();
}

// ── Draining ─────────────────────────────────────────────────────────────
let _draining = false;

/**
 * Try to push queued ops to the server. `force` also retries 'error' ops.
 * Returns { synced, failed, remaining }.
 */
export async function syncOutbox({ force = false } = {}) {
  if (_draining) return { synced: 0, failed: 0, remaining: _state.pending };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    await refreshCount();
    return { synced: 0, failed: 0, remaining: _state.pending };
  }

  _draining = true;
  _state = { ..._state, syncing: true };
  emit();

  let synced = 0;
  let failed = 0;
  try {
    const ops = await listOps();
    for (const op of ops) {
      if (op.status === 'error' && !force) continue; // skip known-bad unless forced
      const handler = HANDLERS[op.type];
      if (!handler) {
        await updateOp(op.id, { status: 'error', error: 'نوع عملية غير معروف' });
        failed++;
        continue;
      }
      try {
        await handler(op.payload);
        await removeOp(op.id);
        synced++;
      } catch (e) {
        if (isNetworkError(e)) {
          // Still offline / dropped — stop and keep everything queued.
          break;
        }
        await updateOp(op.id, {
          status: 'error',
          error: (e && e.message) || 'فشل الرفع',
          tries: (op.tries || 0) + 1,
        });
        failed++;
      }
    }
  } finally {
    _draining = false;
    _state = { ..._state, syncing: false };
    await refreshCount();
  }
  return { synced, failed, remaining: _state.pending };
}

/** Force a full retry including previously-errored ops. */
export function syncNow() {
  return syncOutbox({ force: true });
}

// ── Wiring (call once at app start) ──────────────────────────────────────
let _inited = false;
export function initSync() {
  if (_inited) return;
  _inited = true;
  refreshCount();
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { syncOutbox(); });
    // Best-effort initial drain shortly after load.
    setTimeout(() => { if (navigator.onLine) syncOutbox(); }, 1500);
  }
}

/** Notify the engine that something was just enqueued (refresh + try now). */
export async function notifyEnqueued() {
  await refreshCount();
  if (typeof navigator === 'undefined' || navigator.onLine) syncOutbox();
}
